import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as authService from '../services/authService';
import impersonationService from 'services/impersonationService';
import { getSelectedCompanyCurrencyCode } from 'services/Configuration/companyCurrenciesService';
import { useCurrency } from './CurrencyContext';

const AuthContext = createContext();

const INACTIVITY_LIMIT_MS = 30 * 60 * 1000;
const INACTIVITY_WARNING_MS = 28 * 60 * 1000;
const REFRESH_WINDOW_MS = 30 * 60 * 1000;
const CHECK_INTERVAL_MS = 1 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const { setCurrency } = useCurrency();
  const [error, setError] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [inactivitySecondsLeft, setInactivitySecondsLeft] = useState(null);

  const syncSessionFromStorage = useCallback(async () => {
    const savedToken = authService.getToken();
    const savedUser = authService.getCurrentUser();

    setToken(savedToken || null);
    setUser(savedUser || null);

    if (savedToken && savedUser) {
      try {
        const selectedCurrencyCode = await getSelectedCompanyCurrencyCode();
        setCurrency(selectedCurrencyCode);
      } catch (currencyError) {
        console.error('Error fetching active currency:', currencyError);
        setCurrency('');
      }
    } else {
      setCurrency('');
    }
  }, [setCurrency]);

  useEffect(() => {
    const init = async () => {
      await syncSessionFromStorage();

      setInitializing(false);
    };

    init();
  }, [syncSessionFromStorage]);

  const ACTIVITY_STORAGE_KEY = 'last_activity';
  const refreshingRef = useRef(false);

  const getLastActivity = () => {
    const stored = localStorage.getItem(ACTIVITY_STORAGE_KEY);
    return stored ? parseInt(stored, 10) : Date.now();
  };

  const setLastActivity = (ts) => {
    localStorage.setItem(ACTIVITY_STORAGE_KEY, String(ts));
  };

  useEffect(() => {
    setLastActivity(Date.now());
    const resetActivity = () => {
      setLastActivity(Date.now());
      setInactivitySecondsLeft(null);
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, resetActivity, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, resetActivity));
  }, []);

  const forceLogout = useCallback((reason) => {
    authService.clearSession();
    impersonationService.stopImpersonation();
    localStorage.removeItem(ACTIVITY_STORAGE_KEY);
    setUser(null);
    setToken(null);
    setCurrency('');
    setError(null);
    const params = reason ? `?reason=${reason}` : '';
    window.location.href = `/login${params}`;
  }, [setCurrency]);

  useEffect(() => {
    if (!token || !user) return;

    const check = async () => {
      const now = Date.now();
      const savedToken = authService.getToken();
      const savedUser = authService.getCurrentUser();

      if (!savedToken || !savedUser) {
        forceLogout();
        return;
      }

      const idleMs = now - getLastActivity();

      if (idleMs > INACTIVITY_LIMIT_MS) {
        forceLogout('inactivity');
        return;
      }

      if (idleMs >= INACTIVITY_WARNING_MS) {
        const remaining = Math.ceil((INACTIVITY_LIMIT_MS - idleMs) / 1000);
        setInactivitySecondsLeft(remaining > 0 ? remaining : 0);
      } else {
        setInactivitySecondsLeft(null);
      }

      const expiresAt = savedUser.expiresAt ? new Date(savedUser.expiresAt).getTime() : 0;
      if (expiresAt && now >= expiresAt) {
        forceLogout();
        return;
      }

      // En contexto de reset de contraseña el JWT es de un solo uso: no se debe
      // refrescar (el /Auth/refresh emite un JWT normal sin el claim reset_context
      // y eso rompe el endpoint complete-password-reset). El JWT muere cuando el
      // usuario complete o cancele el flujo.
      const isResetContext = savedUser?.resetContext === true;

      if (!isResetContext && expiresAt && expiresAt - now < REFRESH_WINDOW_MS && !refreshingRef.current) {
        refreshingRef.current = true;
        try {
          const response = await authService.refreshToken();
          setToken(response.token);
          setUser((prev) => prev ? { ...prev, expiresAt: response.expiresAt } : prev);
        } catch {
          forceLogout();
        } finally {
          refreshingRef.current = false;
        }
      }
    };

    check();

    const intervalId = setInterval(check, CHECK_INTERVAL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    const handleStorage = (e) => {
      if (e.key === 'auth_token' || e.key === 'auth_user') check();
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('storage', handleStorage);
    };
  }, [token, user, forceLogout]);

  const login = async (email, password) => {
    setError(null);
    setAuthLoading(true);

    try {
      const response = await authService.login(email, password);

      // MFA pendiente: aún no hay token. Devolvemos la respuesta para que el
      // componente de Login muestre el input del código. Nada de sesión todavía.
      if (response.requiresMfa) {
        return response;
      }

      setLastActivity(Date.now());
      sessionStorage.removeItem('dailyStateCache');

      setToken(response.token);
      setUser({
        userId: response.userId,
        name: response.name,
        email: response.email,
        role: response.role,
        expiresAt: response.expiresAt,
        mustChangePassword: response.mustChangePassword ?? false,
        resetContext: response.resetContext ?? false,
      });

      try {
        const selectedCurrencyCode = await getSelectedCompanyCurrencyCode();
        setCurrency(selectedCurrencyCode);
      } catch (currencyError) {
        console.error('Error fetching active currency:', currencyError);
        setCurrency('');
      }

      return response;
    } catch (err) {
      const errorMsg = err.message || 'Error al iniciar sesión';
      setError(errorMsg);
      throw err;
    } finally {
      setAuthLoading(false);
    }
  };

  const verifyMfa = async (userId, code) => {
    setError(null);
    setAuthLoading(true);

    try {
      const response = await authService.verifyMfa(userId, code);

      setLastActivity(Date.now());
      sessionStorage.removeItem('dailyStateCache');

      setToken(response.token);
      setUser({
        userId: response.userId,
        name: response.name,
        email: response.email,
        role: response.role,
        expiresAt: response.expiresAt,
        mustChangePassword: response.mustChangePassword ?? false,
      });

      try {
        const selectedCurrencyCode = await getSelectedCompanyCurrencyCode();
        setCurrency(selectedCurrencyCode);
      } catch (currencyError) {
        console.error('Error fetching active currency:', currencyError);
        setCurrency('');
      }

      return response;
    } catch (err) {
      const errorMsg = err.message || 'Código inválido o expirado';
      setError(errorMsg);
      throw err;
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = async () => {
    sessionStorage.removeItem('dailyStateCache');
    setAuthLoading(true);
    try {
      await authService.logout();
      impersonationService.stopImpersonation();
    } finally {
      setUser(null);
      setToken(null);
      setCurrency('');
      setError(null);
      setAuthLoading(false);
    }
  };

  const impersonate = async (email) => {
    setError(null);
    setAuthLoading(true);

    try {
      await impersonationService.impersonateByEmail(email);
      await syncSessionFromStorage();
    } finally {
      setAuthLoading(false);
    }
  };

  const stopImpersonation = async () => {
    impersonationService.stopImpersonation();
    await syncSessionFromStorage();
  };

  const clearMustChangePassword = useCallback(() => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, mustChangePassword: false, resetContext: false };
      localStorage.setItem('auth_user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const value = {
    user,
    token,
    error,
    isAuthenticated: !!token && !!user,
    mustChangePassword: user?.mustChangePassword ?? false,
    resetContext: user?.resetContext ?? false,
    login,
    verifyMfa,
    logout,
    initializing,
    authLoading,
    impersonate,
    stopImpersonation,
    clearMustChangePassword,
    inactivitySecondsLeft,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};
